import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROLE_COLORS, STATUS_LABELS, STATUS_COLORS, useUser } from '../UserContext';

const API = `${window.location.protocol}//${window.location.hostname}/api`;

function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(async r => {
    const data = await r.json().catch(() => null);
    if (!r.ok) throw new Error(data?.error || 'Request failed');
    return data;
  });
}

function StatCard({ label, value, color = '#3b82f6', sub }) {
  return (
    <div style={{ backgroundColor: '#1e293b', border: `1px solid ${color}22`, borderTop: `3px solid ${color}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ color, fontSize: 26, fontWeight: 800 }}>{value ?? '—'}</div>
      {sub && <div style={{ color: '#64748b', fontSize: 11, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function JhaComplianceTable({ jhas }) {
  if (!jhas.length) return (
    <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 24, border: '1px dashed #334155', borderRadius: 8 }}>No JHAs recorded yet.</div>
  );
  return (
    <div style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ backgroundColor: '#111827' }}>
            {['Tech', 'Work Order', 'Station', 'Status', 'Submitted', 'Lag (min)'].map(h => (
              <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid #1e293b' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jhas.map(j => {
            const lagColor = j.jha_lag_minutes == null ? '#64748b' : j.jha_lag_minutes <= 15 ? '#22c55e' : j.jha_lag_minutes <= 30 ? '#f59e0b' : '#ef4444';
            const statusColor = { draft: '#64748b', pre_job_complete: '#3b82f6', post_job_complete: '#22c55e' }[j.status] || '#64748b';
            return (
              <tr key={j.id} style={{ borderBottom: '1px solid #111827' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#111827'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <td style={{ padding: '9px 12px', color: '#e2e8f0', fontWeight: 700 }}>{j.conducted_by_name || '—'}</td>
                <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{j.work_item_title || `JHA-${j.id}`}</td>
                <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{j.station || '—'}</td>
                <td style={{ padding: '9px 12px' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4, backgroundColor: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}44` }}>
                    {j.status?.replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={{ padding: '9px 12px', color: '#94a3b8' }}>
                  {j.jha_submitted_at ? new Date(j.jha_submitted_at).toLocaleString() : '—'}
                </td>
                <td style={{ padding: '9px 12px', color: lagColor, fontWeight: 700 }}>
                  {j.jha_lag_minutes != null ? j.jha_lag_minutes : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FmpDiscrepancyTable({ verifs }) {
  if (!verifs.length) return (
    <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 24, border: '1px dashed #334155', borderRadius: 8 }}>No FMP verifications recorded.</div>
  );
  return (
    <div style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ backgroundColor: '#111827' }}>
            {['Tech', 'Checks', 'Passed', 'Discrepancies', 'Last Check'].map(h => (
              <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid #1e293b' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {verifs.map(v => (
            <tr key={v.tech_id} style={{ borderBottom: '1px solid #111827' }}>
              <td style={{ padding: '9px 12px', color: '#e2e8f0', fontWeight: 700 }}>{v.tech_name}</td>
              <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{v.total_checks}</td>
              <td style={{ padding: '9px 12px', color: '#22c55e', fontWeight: 700 }}>{v.passed}</td>
              <td style={{ padding: '9px 12px' }}>
                <span style={{ color: v.discrepancies > 0 ? '#ef4444' : '#22c55e', fontWeight: 800 }}>
                  {v.discrepancies}
                </span>
              </td>
              <td style={{ padding: '9px 12px', color: '#64748b' }}>
                {v.last_check_at ? new Date(v.last_check_at).toLocaleDateString() : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TeamWorkTable({ workItems, navigate }) {
  if (!workItems.length) return (
    <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 24, border: '1px dashed #334155', borderRadius: 8 }}>No active work items.</div>
  );
  return (
    <div style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ backgroundColor: '#111827' }}>
            {['Work Order', 'Title', 'Location', 'Status', 'Tech', 'Scheduled'].map(h => (
              <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid #1e293b' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {workItems.map(w => {
            const sc = STATUS_COLORS[w.status] || '#64748b';
            return (
              <tr key={w.id} style={{ borderBottom: '1px solid #111827', cursor: 'pointer' }}
                onClick={() => navigate(`/work/${w.id}`)}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#111827'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <td style={{ padding: '9px 12px', color: '#3b82f6', fontWeight: 700 }}>{w.wo_number || `#${w.id}`}</td>
                <td style={{ padding: '9px 12px', color: '#e2e8f0', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.title}</td>
                <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{w.location_name || '—'}</td>
                <td style={{ padding: '9px 12px' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4, backgroundColor: `${sc}22`, color: sc, border: `1px solid ${sc}44` }}>
                    {STATUS_LABELS[w.status] || w.status}
                  </span>
                </td>
                <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{w.tech_name || '—'}</td>
                <td style={{ padding: '9px 12px', color: '#64748b' }}>{w.scheduled_date ? new Date(w.scheduled_date).toLocaleDateString() : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const TABS = [
  { key: 'work',   label: 'Team Work' },
  { key: 'jha',    label: 'JHA Compliance' },
  { key: 'fmp',    label: 'FMP Checks' },
];

export default function FieldManagerView() {
  const user = useUser();
  const navigate = useNavigate();
  const [tab, setTab] = useState('work');
  const [workItems, setWorkItems] = useState([]);
  const [jhas, setJhas] = useState([]);
  const [fmpSummary, setFmpSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [wi, jhaRows, fmpRows] = await Promise.all([
        api('/work-items?status=in_progress,ready_to_work,fmp_pending').catch(() => []),
        api('/jhas').catch(() => []),
        api('/fmp-verifications/summary').catch(() => []),
      ]);
      setWorkItems(Array.isArray(wi) ? wi : wi?.items || []);
      setJhas(Array.isArray(jhaRows) ? jhaRows : []);
      setFmpSummary(Array.isArray(fmpRows) ? fmpRows : []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const MANAGER_ROLES = new Set(['admin', 'managing_director', 'director', 'field_manager', 'rpm']);
  if (!MANAGER_ROLES.has(user?.role)) {
    return (
      <div style={{ flex: 1, backgroundColor: '#0f172a', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' }}>
        Field Manager view is not available for your role.
      </div>
    );
  }

  // Stats
  const activeWork = workItems.filter(w => w.status === 'in_progress').length;
  const jhasToday = jhas.filter(j => {
    const d = j.created_at ? new Date(j.created_at) : null;
    return d && d.toDateString() === new Date().toDateString();
  }).length;
  const totalDiscrepancies = fmpSummary.reduce((sum, r) => sum + (r.discrepancies || 0), 0);
  const avgLag = jhas.filter(j => j.jha_lag_minutes != null).reduce((sum, j, _, arr) => sum + j.jha_lag_minutes / arr.length, 0);

  return (
    <div style={{ flex: 1, overflow: 'hidden', backgroundColor: '#0f172a', fontFamily: 'sans-serif', color: '#e2e8f0', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid #1e293b', padding: '16px 20px' }}>
        <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 20 }}>Field Manager</div>
        <div style={{ color: '#64748b', fontSize: 13, marginTop: 3 }}>Team safety & work status overview</div>
      </div>

      {/* Stats row */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #1e293b' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          <StatCard label="Active Work" value={activeWork} color="#3b82f6" />
          <StatCard label="JHAs Today" value={jhasToday} color="#22c55e" />
          <StatCard label="Avg JHA Lag" value={jhas.some(j => j.jha_lag_minutes != null) ? `${Math.round(avgLag)}m` : '—'} color={avgLag > 30 ? '#ef4444' : '#22c55e'} sub="site arrival to submission" />
          <StatCard label="FMP Discrepancies" value={totalDiscrepancies} color={totalDiscrepancies > 0 ? '#ef4444' : '#22c55e'} />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', padding: '0 20px' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '12px 16px', border: 'none',
            borderBottom: tab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
            backgroundColor: 'transparent',
            color: tab === t.key ? '#93c5fd' : '#64748b',
            cursor: 'pointer', fontSize: 13,
            fontWeight: tab === t.key ? 800 : 600,
          }}>{t.label}</button>
        ))}
        <button onClick={load} style={{ marginLeft: 'auto', padding: '10px 14px', backgroundColor: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13 }}>↻ Refresh</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {err && <div style={{ color: '#f87171', marginBottom: 14, fontSize: 13 }}>{err}</div>}
        {loading ? (
          <div style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>Loading…</div>
        ) : (
          <>
            {tab === 'work' && <TeamWorkTable workItems={workItems} navigate={navigate} />}
            {tab === 'jha'  && <JhaComplianceTable jhas={jhas} />}
            {tab === 'fmp'  && <FmpDiscrepancyTable verifs={fmpSummary} />}
          </>
        )}
      </div>
    </div>
  );
}
